import {ErrorWithData} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {isTeamNamespace, parseTeammateNamespace} from '../../../../helpers/models/namespaceHelpers';
import {TagBehaviorsEnum, TagModel} from '../../../../models/api/tagModel';
import {
  InboxesFiltersEnum,
  InboxesSearchLocationsEnum,
  TeammatesFoldersEnum,
  TeammatesMessageFoldersEnum,
  TeamsFoldersEnum,
} from '../../../../models/url/inboxesUrlModel';
import {AppState} from '../../../appState';
import {ResourceMap} from '../../../appTypes';
import {getStarredTagIdsToTeammateMap} from '../../../data/tags/tagsSelectors';
import {getUser} from '../../../data/teammates/teammatesSelectors';
import {makeGetIsCellsEmptySetupActionsActive} from '../../../stateless/cellsEmptySetupActionsSelectors';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../inboxes/parameters/inboxesParameters';
import {emptyInboxesSelection, findParametersViewId} from '../../inboxes/parameters/inboxesParametersHelpers';
import {buildParametersPath} from '../../inboxes/parameters/inboxesParametersPathHelpers';
import {CellsContext, CellsContextTypesEnum} from './cellsContext';

export function makeCellsContext(state: AppState, parameters: InboxesParameters): CellsContext {
  const {userId, teammates, inboxes, tags, customViews} = state.data;

  // Type.
  const type = findContextType(tags, parameters);

  // Path.
  const path = buildParametersPath({
    ...parameters,
    selection: emptyInboxesSelection,
    activity: undefined,
  });

  // Several components assume this path does not have a query string and will concatenate/append path segments.
  if (path.includes('?')) {
    throw new ErrorWithData('Unexpected query string when making the cells context', {
      path,
      context_type: type,
    });
  }

  // Starred tag IDs
  const starredTagIdsToTeammateMap = getStarredTagIdsToTeammateMap(state);

  // Teammate ID.
  const teammateInboxId = parameters.type === InboxesParametersTypesEnum.TEAMMATE_INBOX && parameters.inboxId;
  const teammateInbox = teammateInboxId && inboxes.byId[teammateInboxId];
  const teammateIdFromInbox = teammateInbox && parseTeammateNamespace(teammateInbox.namespace);

  const teammateViewId = findParametersViewId(parameters);
  const teammateView = teammateViewId && customViews[teammateViewId];
  const teammateIdFromView = teammateView && parseTeammateNamespace(teammateView.namespace);
  const delegatedStarredTagOwnerId =
    teammateViewId &&
    !!starredTagIdsToTeammateMap[teammateViewId] &&
    starredTagIdsToTeammateMap[teammateViewId];
  const delegatedTeammateId = parameters.type === InboxesParametersTypesEnum.USER && parameters.teammateId;

  const teammateId =
    delegatedStarredTagOwnerId || delegatedTeammateId || teammateIdFromInbox || teammateIdFromView || userId;
  const teammate = teammates[teammateId] || getUser(state);

  // Inbox ID.
  const inboxId =
    ((parameters.type === InboxesParametersTypesEnum.TEAMMATE_INBOX ||
      parameters.type === InboxesParametersTypesEnum.INBOX) &&
      parameters.inboxId) ||
    undefined;
  const inbox = inboxId && inboxes.byId[inboxId];

  const namespace =
    // By checking `inbox` here we are implicitely checking that the type is `TEAMMATE_INBOX` or `INBOX`.
    (inbox && inbox.namespace) ||
    (teammateView && teammateView.namespace) ||
    // Covers the user inbox and delegated teammate inbox cases.
    (delegatedTeammateId && `tea:${delegatedTeammateId}`) ||
    // Covers team workspace headers.
    (parameters.type === InboxesParametersTypesEnum.TEAM &&
      parameters.teamId &&
      `tim:${parameters.teamId}`) ||
    undefined;

  // Search query.
  const searchQuery =
    (parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES &&
      parameters.search &&
      parameters.search.query) ||
    undefined;

  // Special folders.
  const inTrash =
    (parameters.type === InboxesParametersTypesEnum.USER &&
      parameters.folder === TeammatesFoldersEnum.TRASHED) ||
    (parameters.type === InboxesParametersTypesEnum.TEAM && parameters.folder === TeamsFoldersEnum.TRASHED) ||
    (parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES &&
      parameters.filters.includes(InboxesFiltersEnum.TRASHED)) ||
    isSearchQueryHasStatusFilter(searchQuery, ['trashed', 'deleted']);

  const inSpam =
    (parameters.type === InboxesParametersTypesEnum.USER &&
      parameters.folder === TeammatesFoldersEnum.SPAM) ||
    (parameters.type === InboxesParametersTypesEnum.TEAM && parameters.folder === TeamsFoldersEnum.SPAM) ||
    (parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES &&
      parameters.filters.includes(InboxesFiltersEnum.SPAM)) ||
    isSearchQueryHasStatusFilter(searchQuery, ['spam']);

  const inDrafts =
    parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES &&
    parameters.folder === TeammatesMessageFoldersEnum.DRAFTS;

  const inSent =
    parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES &&
    parameters.folder === TeammatesMessageFoldersEnum.SENT;

  const isAssignedShared =
    parameters.type === InboxesParametersTypesEnum.TEAMMATE &&
    parameters.folder === TeammatesFoldersEnum.ASSIGNED_SHARED;

  const isDelegation = teammateId !== userId;

  const inOpenFilter =
    parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES &&
    (parameters.filters.includes(InboxesFiltersEnum.OPEN) ||
      parameters.filters.includes(InboxesFiltersEnum.UNASSIGNED));

  const isCellsEmptySetupActionsActive = makeGetIsCellsEmptySetupActionsActive()(state, parameters);

  return {
    type,
    path,
    teammateId,
    namespace,
    teammateInboxTagId: teammate.inboxTagId,
    inboxId,
    searchQuery,
    inTrash,
    inDrafts,
    inSent,
    inSpam,
    isAssignedShared,
    isDelegation,
    inOpenFilter,
    isCellsEmptySetupActionsActive,
  };
}

function findContextType(tags: ResourceMap<TagModel>, parameters: InboxesParameters) {
  const workspaceContextType = findWorkspaceContextType(tags, parameters);

  // If we're not in search mode, use the workspace context type.
  if (!hasSearchInInboxesParameters(parameters)) {
    return workspaceContextType;
  }

  // If the search is local, use the workspace context type.
  if (parameters.search.location !== InboxesSearchLocationsEnum.GLOBAL) {
    return workspaceContextType;
  }

  // Global search can be one of two mixed contexts depending on the workspace.
  return workspaceContextType === CellsContextTypesEnum.TEAMMATE
    ? CellsContextTypesEnum.MIXED_SEARCH_GLOBAL_FROM_TEAMMATE
    : CellsContextTypesEnum.MIXED_SEARCH_GLOBAL_FROM_TEAM;
}

const teammateContextTypes = [
  InboxesParametersTypesEnum.TEAMMATE_MESSAGES,
  InboxesParametersTypesEnum.TEAMMATE_INBOX,
  InboxesParametersTypesEnum.USER,
];

function findWorkspaceContextType(tags: ResourceMap<TagModel>, parameters: InboxesParameters) {
  switch (parameters.type) {
    // In the case of a custom view, we may want to check the context of the tag we're viewing.
    // NOTE: this will need to be modified whenever we have custom views who are not only tied to tags.
    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW: {
      if (!isTeamTagView(parameters.viewId, tags)) {
        return CellsContextTypesEnum.TEAMMATE;
      }

      if (tags[parameters.viewId]?.behaviors.includes(TagBehaviorsEnum.CAN_CUSTOM_VIEW)) {
        return CellsContextTypesEnum.CUSTOM_VIEW;
      }

      return CellsContextTypesEnum.MIXED_TEAM_TAG;
    }

    default:
      return teammateContextTypes.includes(parameters.type)
        ? CellsContextTypesEnum.TEAMMATE
        : CellsContextTypesEnum.TEAM;
  }
}

function isTeamTagView(viewId: number, tags: ResourceMap<TagModel>) {
  const existingTag = tags[viewId];
  if (!existingTag) {
    return false;
  }

  return isTeamNamespace(existingTag.namespace);
}

function isSearchQueryHasStatusFilter(searchQuery: string | undefined, modifiers: ReadonlyArray<string>) {
  if (!searchQuery) {
    return false;
  }

  const lowerCaseQuery = searchQuery.toLowerCase();
  return modifiers.some((modifier) => lowerCaseQuery.includes(`is:${modifier}`));
}
